/** Sieve — i18n runtime (§11). Auto-detect device language, manual override. */

import type { Dict } from './types';
import { en } from './en';
import { fr } from './fr';

export type { Dict };
export type LangCode = 'en' | 'fr';

export const DICTS: Record<LangCode, Dict> = { en, fr };
export const SUPPORTED: LangCode[] = ['en', 'fr'];

/** Resolves a preference ('auto' | code) to a concrete supported language. */
export function resolveLang(pref: string): LangCode {
  if (pref === 'en' || pref === 'fr') return pref;
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'en';
  const primary = (nav || 'en').slice(0, 2).toLowerCase();
  return primary === 'fr' ? 'fr' : 'en';
}

export function dict(pref: string): Dict {
  return DICTS[resolveLang(pref)];
}

/** Minimal `{token}` interpolation. */
export function t(template: string, vars: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

/**
 * Deterministically picks one of the 5 messages for an outcome tier (§3.2),
 * keyed by puzzle id so the same puzzle always shows the same line (stable
 * across reloads) but different puzzles vary.
 */
export function outcomeMessage(d: Dict, tier: 'flawless' | 'solved' | 'failed', seed: number): string {
  const pool = d.messages[tier];
  return pool[Math.abs(seed) % pool.length]!;
}

/** Outcome message for a specific mode's pool (prime or multiples). */
export function outcomeMessageFor(
  d: Dict,
  mode: 'prime' | 'multiples',
  tier: 'flawless' | 'solved' | 'failed',
  seed: number,
): string {
  const pool = mode === 'multiples' ? d.mult.messages[tier] : d.messages[tier];
  return pool[Math.abs(seed) % pool.length]!;
}

/** Locale-aware date formatting (§11), no digit grouping inside grid cells. */
export function formatDate(iso: string, lang: LangCode): string {
  const [y, m, day] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(y, m - 1, day);
  try {
    return new Intl.DateTimeFormat(lang, { dateStyle: 'long' }).format(date);
  } catch {
    return iso;
  }
}
